import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, InlineNotification} from '@veneer/core';

import {LicenseUploadImport, ResponseError} from '@br/idm-api';
import {useCustomerApi} from '@br/idm-components';

import {flattenErrorResponse} from '../../../util';
import {LicenseImportStepProps} from '../LicenseImportSteps/types';

import useStyles from './styles';

const LicenseImportForm = ({csvFileContent, handleOnClose}: LicenseImportStepProps): JSX.Element => {
    const {t} = useTranslation(['licenses']);
    const styles = useStyles();
    const {supportApi} = useCustomerApi();
    const [isImporting, setIsImporting] = useState(false);
    const [importingFinished, setImportingFinished] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const [importResult, setImportResult] = useState<LicenseUploadImport>({licenseCount: 0, deviceCount: 0});

    const importLicenses = useCallback(() => {
        setIsImporting(true);
        setImportingFinished(false);
        setImportError(null);

        supportApi
            .supportLicenseImportLicensesCreate({
                licenseUploadImportRequest: {
                    csvFileContent: csvFileContent,
                },
            })
            .then(
                response => {
                    setIsImporting(false);
                    setImportingFinished(true);
                    setImportError(null);

                    setImportResult(response);
                },
                async (error: ResponseError) => {
                    const errors = await flattenErrorResponse(error);
                    setIsImporting(false);
                    setImportingFinished(true);
                    setImportError(errors.join(', '));
                }
            );
    }, [csvFileContent, supportApi]);

    if (!importingFinished) {
        return (
            <>
                <p className={styles.prompt}>
                    {t('licenses:importLicensesDescription', 'Select confirm to carry out license extension operation')}
                </p>
                <Button
                    disabled={isImporting || csvFileContent === ''}
                    onClick={importLicenses}
                >
                    {t('licenses:confirm', 'Confirm')}
                </Button>
            </>
        );
    }

    if (importError) {
        return (
            <InlineNotification
                type="negative"
                title={t('licenses:importLicensesFailure', 'Licenses failed to import')}
                i18n={{}}
                closeButton={false}
                description={importError}
            />
        );
    }

    return (
        <>
            <InlineNotification
                type="positive"
                title={t('licenses:importLicensesSuccess', 'Licenses imported successfully')}
                i18n={{}}
                closeButton={false}
                description={t(
                    'licenses:importLicensesSuccessDescription',
                    'Licenses used: {{licenseCount}}, Devices updated: {{deviceCount}}',
                    {licenseCount: importResult.licenseCount, deviceCount: importResult.deviceCount}
                )}
            />

            <Button
                appearance="primary"
                onClick={handleOnClose}
            >
                {t('licenses:finish', 'Finish')}
            </Button>
        </>
    );
};

export default LicenseImportForm;
